import { BUILDER_PARTS, builderQuery, CROSSHAIR_CODE, readConsoleValues, type BuilderPart, type ConfigCategory } from "@69ly/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../components/Icon.tsx";
import { Link, useNavigate } from "react-router";
import { CopyButton } from "../components/CopyButton.tsx";
import { Banner } from "../components/Status.tsx";
import { useCreateConfig } from "../lib/config-queries.ts";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { errorText } from "../lib/errors.ts";

const PARTS = Object.keys(BUILDER_PARTS) as BuilderPart[];

/**
 * Builds a config from the player's own settings: the crosshair from its share
 * code, the rest by pasting what the console prints for a line of setting names.
 */
export function ConfigBuilderPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { scope, team } = useCurrentTeam();
  const create = useCreateConfig();
  const [code, setCode] = useState("");
  const [printed, setPrinted] = useState<Record<BuilderPart, string>>({ viewmodel: "", bob: "", radar: "", hud: "" });
  const [use, setUse] = useState<Record<BuilderPart | "crosshair", boolean>>({ crosshair: true, viewmodel: true, bob: true, radar: true, hud: true });
  const [title, setTitle] = useState("");

  const codeOk = CROSSHAIR_CODE.test(code.trim());
  const values = Object.fromEntries(PARTS.map((p) => [p, readConsoleValues(p, printed[p])])) as Record<BuilderPart, { name: string; value: string }[]>;
  const blocks: string[] = [];
  if (use.crosshair && codeOk) blocks.push(`// ${t("builder.part.crosshair")}\napply_crosshair_code ${code.trim()}`);
  for (const p of PARTS) if (use[p] && values[p].length) blocks.push([`// ${t(`builder.part.${p}`)}`, ...values[p].map((v) => `${v.name} "${v.value}"`)].join("\n"));
  const text = blocks.length ? `// ${t("builder.header")}\n${blocks.join("\n\n")}\n` : "";
  const parts = [...(use.crosshair && codeOk ? ["crosshair"] : []), ...PARTS.filter((p) => use[p] && values[p].length)];
  const category: ConfigCategory = parts.length === 1 && parts[0] === "crosshair" ? "crosshair" : parts.every((p) => p === "viewmodel" || p === "bob") ? "viewmodel" : "other";
  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: "application/octet-stream" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(title.trim() || "my_settings").toLowerCase().replace(/[^a-z0-9]+/g, "_")}.cfg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page config-builder">
      <div>
        <Link to="/configs" className="back-link">
          <Icon name="chevronLeft" size={16} />{t("configs.title")}
        </Link>
        <h1 className="page-title">{t("builder.title")}</h1>
        <p className="muted">{t("builder.intro")}</p>
      </div>
      {create.error && <Banner tone="warning">{errorText(t, create.error)}</Banner>}
      <div className="columns">
        <div className="column-main builder-parts">
          <section className="card">
            <label className="check builder-use">
              <input type="checkbox" checked={use.crosshair} onChange={(e) => setUse({ ...use, crosshair: e.target.checked })} />
              <h2 className="panel-title">{t("builder.part.crosshair")}</h2>
            </label>
            <p className="muted small">{t("builder.crosshairHelp")}</p>
            <label className="field">
              <span>{t("builder.codeLabel")}</span>
              <input className="mono" value={code} onChange={(e) => setCode(e.target.value)} placeholder="CSGO-xxxxx-xxxxx-xxxxx-xxxxx-xxxxx" spellCheck={false} />
            </label>
            {code.trim() && !codeOk && <p className="field-error small">{t("builder.codeBad")}</p>}
          </section>
          {PARTS.map((p, i) => (
            <section className="card" key={p}>
              <label className="check builder-use">
                <input type="checkbox" checked={use[p]} onChange={(e) => setUse({ ...use, [p]: e.target.checked })} />
                <h2 className="panel-title">{t(`builder.part.${p}`)}</h2>
              </label>
              <ol className="builder-steps small">
                <li>
                  {t("builder.step1")}
                  <span className="command">
                    <code>{builderQuery(p)}</code>
                    <CopyButton text={builderQuery(p)} />
                  </span>
                </li>
                <li>{t("builder.step2")}</li>
              </ol>
              <label className="field">
                <span className="visually-hidden">{t("builder.pasteLabel", { part: t(`builder.part.${p}`) })}</span>
                <textarea
                  className="mono cfg-text"
                  rows={4}
                  value={printed[p]}
                  aria-label={t("builder.pasteLabel", { part: t(`builder.part.${p}`) })}
                  onChange={(e) => setPrinted({ ...printed, [p]: e.target.value })}
                  placeholder={i === 0 ? "viewmodel_fov = 68\nviewmodel_offset_x = 2.5" : undefined}
                  spellCheck={false}
                />
              </label>
              {printed[p].trim() && (
                <p className="small muted">{t("builder.found", { count: values[p].length, total: BUILDER_PARTS[p].length })}</p>
              )}
            </section>
          ))}
        </div>
        <aside className="column-side">
          <section className="card builder-result">
            <h2 className="panel-title">{t("builder.result")}</h2>
            {text ? (
              <>
                <pre className="cfg-preview">{text}</pre>
                <label className="field">
                  <span>{t("form.titleLabel")}</span>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={60} placeholder={t("builder.titlePlaceholder")} />
                </label>
                <div className="row">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!title.trim() || create.isPending}
                    onClick={() => create.mutate({ scope, title, description: "", category, text, overrides: {} }, { onSuccess: (c) => navigate(`/configs/${c.id}`) })}
                  >
                    {t("builder.save", { library: team?.name ?? t("nav.personal") })}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={download}>
                    {t("configs.download")}
                  </button>
                </div>
              </>
            ) : (
              <p className="muted small">{t("builder.nothingYet")}</p>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}
